import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getCategoriesData } from "../../services/HomeServices";
import { toast } from "react-toastify";
import _ from "lodash";
import "./CategoryNavigation.css";

// Background colors for category letter avatars
const avatarColors = [
  "#7c3aed", "#e91e63", "#8b5cf6", "#673ab7",
  "#3f51b5", "#2196f3", "#00bcd4", "#009688",
  "#4caf50", "#8bc34a", "#ff9800", "#ff5722",
  "#795548", "#607d8b", "#f44336", "#00acc1"
];

// Get consistent color based on category id
const getAvatarColor = (id) => {
  const index = id % avatarColors.length;
  return avatarColors[index];
};

const CategoryNavigation = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [failedImages, setFailedImages] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategoryId = searchParams.get("category_id");

  // Handle image load error
  const handleImageError = (categoryId) => {
    setFailedImages(prev => ({ ...prev, [categoryId]: true }));
  };

  // Get first letter of category name
  const getFirstLetter = (name) => {
    return name ? name.charAt(0).toUpperCase() : "?";
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      console.log("Fetching categories...");
      const response = await getCategoriesData();
      console.log("Categories response:", response);
      console.log("Categories data:", response?.data);
      setCategories(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      console.error("Error details:", error?.response?.data || error.message);
      toast("Failed to load categories", { type: "error" });
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryClick = (categoryId) => {
    if (selectedCategoryId === String(categoryId)) {
      return;
    }
    const newParams = new URLSearchParams(searchParams);
    newParams.set("category_id", categoryId);
    newParams.delete("subcategory_id");
    setSearchParams(newParams);
  };

  const handleAllProductsClick = () => {
    setSearchParams({});
  };

  if (isLoading) {
    return (
      <section className="category-navigation">
        <div className="category-container">
          <div className="loading-container">
            <div className="modern-spinner"></div>
          </div>
        </div>
      </section>
    );
  }

  if (_.isEmpty(categories)) {
    return (
      <section className="category-navigation">
        <div className="category-container">
          <div className="section-header">
            <div className="section-title">
              <div className="section-icon">
                <i className="far fa-th-large"></i>
              </div>
              <h2>Shop by Category</h2>
            </div>
          </div>
          <div className="empty-state-small" style={{ marginTop: '20px' }}>
            <i className="far fa-info-circle"></i>
            <span>No categories available at the moment. Please add categories in the admin panel.</span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="category-navigation">
      <div className="category-container">
        <div className="section-header">
          <div className="section-title">
            <div className="section-icon">
              <i className="far fa-th-large"></i>
            </div>
            <h2>Shop by Category</h2>
          </div>
        </div>

        <div className="category-scroll-wrapper">
          <button
            className={`category-card ${!selectedCategoryId ? "active" : ""}`}
            onClick={handleAllProductsClick}
          >
            <div className="category-icon-wrapper">
              <i className="far fa-grid"></i>
            </div>
            <h3 className="category-name">All Products</h3>
          </button>

          {categories.map((category) => {
            const showLetterAvatar = !category.image || failedImages[category.id];
            return (
              <button
                key={category.id}
                className={`category-card ${
                  selectedCategoryId === String(category.id) ? "active" : ""
                }`}
                onClick={() => handleCategoryClick(category.id)}
              >
                <div className="category-image-wrapper">
                  {showLetterAvatar ? (
                    <div
                      className="category-letter-avatar"
                      style={{ backgroundColor: getAvatarColor(category.id) }}
                    >
                      {getFirstLetter(category.name)}
                    </div>
                  ) : (
                    <img
                      src={category.image}
                      alt={category.name}
                      loading="lazy"
                      onError={() => handleImageError(category.id)}
                    />
                  )}
                </div>
                <h3 className="category-name">{category.name}</h3>
                {category.description && (
                  <p className="category-description">{category.description}</p>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CategoryNavigation;
